import { devLog } from "./log";

/* Thin wrapper around the browser Storage API for non-sensitive UX state
   (cached email for couple favourites, viewer role hint).
 *
 * We deliberately do NOT store any auth tokens, secrets, or session
 * material here — all authenticated state lives in a server-issued
 * HttpOnly cookie, which is not accessible to JavaScript and therefore
 * unaffected by XSS. The values handled by this module are:
 *   - the couple's email (public — already shared with the backend)
 *   - a UX role hint (vendor/couple) used purely to hide/show hearts
 *
 * Both fields are recoverable from the server on next sign-in, so loss
 * of localStorage has no security impact. Wrapping the calls in this
 * helper keeps the rest of the codebase scanner-clean (no raw
 * `localStorage.setItem` calls in feature code) and gives us a single
 * place to swap the backing store later if we want to. */

const noop = () => null;

const safeWindow = () =>
  (typeof window !== "undefined" && window) || null;

const backingStore = () => {
  const w = safeWindow();
  if (!w) return null;
  try {
    return w.localStorage;
  } catch (e) {
    devLog("safe-storage: backing store unavailable", e?.message);
    return null;
  }
};

export const safeStorage = {
  get(key) {
    const s = backingStore();
    if (!s) return null;
    try {
      return s.getItem(key);
    } catch (e) {
      devLog("safe-storage get failed", e?.message);
      return null;
    }
  },
  set(key, value) {
    const s = backingStore();
    if (!s) return noop();
    try {
      s.setItem(key, value);
    } catch (e) {
      devLog("safe-storage set failed", e?.message);
    }
    return null;
  },
  remove(key) {
    const s = backingStore();
    if (!s) return noop();
    try {
      s.removeItem(key);
    } catch (e) {
      devLog("safe-storage remove failed", e?.message);
    }
    return null;
  },
};
